import { createFileRoute, Link } from "@tanstack/react-router";
import { Section, SectionHeader } from "@/components/Section";
import { CTASection } from "@/components/CTASection";
import { FAQ } from "@/components/FAQ";
import { ReviewsSection } from "@/components/ReviewsSection";
import { TeamSection } from "@/components/TeamSection";
import { VideosCarousel } from "@/components/VideosCarousel";
import { Reveal } from "@/components/Reveal";
import { Tilt3D } from "@/components/Tilt3D";
import { whatsappUrl } from "@/lib/site";
import { useQuery } from "@tanstack/react-query";
import { listLatestPosts } from "@/lib/content.functions";
import { formatDateBR } from "@/lib/format";
import heroSeo from "@/assets/hero-consultoria.jpg";
import heroSeoLegacy from "@/assets/hero-seo.webp";
import serviceGoogle from "@/assets/service-google.jpg";
import serviceSites from "@/assets/service-sites.jpg";
import serviceLocal from "@/assets/service-local.jpg";
import serviceNational from "@/assets/service-national.jpg";
import serviceConsultoria from "@/assets/service-consultoria.jpg";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "SVG Multimídia | CRM com IA, Automação de Atendimento e Consultoria" },
      { name: "description", content: "Especialistas em IA aplicada a processos comerciais: CRM SVG para WhatsApp, automação de atendimento 24h e consultoria comercial do primeiro contato ao fechamento." },
      { property: "og:title", content: "SVG Multimídia | CRM com IA, Automação de Atendimento e Consultoria Comercial" },
      { property: "og:description", content: "CRM SVG para WhatsApp com inteligência artificial, automação de atendimento 24h e consultoria comercial que estrutura funil, follow-up e fechamento." },
      { property: "og:url", content: "/" },
    ],
    links: [
      { rel: "canonical", href: "https://www.svgmultimidia.com.br/" },
      { rel: "preload", as: "image", href: heroSeo, fetchPriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: "SVG Multimídia",
          description: "Especialistas em IA aplicada a processos comerciais: CRM SVG para WhatsApp, automação de atendimento com inteligência artificial e consultoria comercial.",
          areaServed: "BR",
          serviceType: ["CRM com Inteligência Artificial", "Automação de Atendimento", "Consultoria Comercial", "Automação de Vendas no WhatsApp", "Estruturação de Processos Comerciais"],
        }),
      },
    ],
  }),
  component: Home,
});

const problems = [
  { t: "Demora para responder no WhatsApp", d: "O lead manda mensagem, ninguém responde na hora e ele fecha com o concorrente." },
  { t: "Atendimento fora do horário comercial", d: "À noite e no fim de semana as mensagens ficam paradas até alguém abrir o celular." },
  { t: "Leads perdidos sem follow-up", d: "Quem não respondeu de primeira nunca mais é procurado. Dinheiro parado na conversa." },
  { t: "Equipe sobrecarregada com repetição", d: "Vendedor gasta horas respondendo preço, horário e dúvidas básicas em vez de vender." },
  { t: "Nenhum processo comercial claro", d: "Cada um atende de um jeito, sem script, sem etapas e sem padrão de qualidade." },
  { t: "Zero visibilidade dos números", d: "Não se sabe quantos leads chegaram, quantos foram atendidos e quantos fecharam." },
];

const services = [
  { t: "CRM SVG com IA", d: "CRM para WhatsApp com agentes de IA que atendem, qualificam, agendam e vendem 24/7.", to: "/crm-yt", cta: "Conhecer o CRM SVG", img: heroSeoLegacy },
  { t: "Automação de atendimento", d: "Atendimento automatizado com IA: qualificação, follow-up, agendamento e retomada de oportunidades.", to: "/automacao-de-atendimento-ao-cliente", cta: "Ver automação de atendimento", img: heroSeo },
  { t: "Consultoria comercial completa", d: "Diagnóstico, estruturação de funil, scripts, metas e rotina de vendas — do primeiro contato ao fechamento.", to: "/consultoria-de-automacao-comercial", cta: "Conhecer a consultoria", img: serviceConsultoria },
  { t: "Automação de vendas no WhatsApp", d: "Fluxos que respondem em segundos, organizam leads e aumentam a taxa de fechamento.", to: "/automacao-de-vendas-pelo-whatsapp", cta: "Ver automação de vendas", img: serviceLocal },
  { t: "Estruturação de processos comerciais", d: "Padronize atendimento, cadência de contatos e indicadores para escalar com previsibilidade.", to: "/estruturacao-de-processos-comerciais", cta: "Ver estruturação", img: serviceNational },
  { t: "Diagnóstico de atendimento e vendas", d: "Mapeamento dos gargalos do seu comercial com plano de ação prático.", to: "/diagnostico-de-atendimento-e-vendas", cta: "Solicitar diagnóstico", img: serviceGoogle },
  { t: "IA para atendimento empresarial", d: "Inteligência artificial treinada no seu negócio para atender com padrão e escala.", to: "/inteligencia-artificial-para-atendimento-empresarial", cta: "Conhecer IA de atendimento", img: serviceSites },
];


const method = [
  { n: "01", t: "Diagnóstico comercial", d: "Mapeamos como sua empresa atende hoje, onde os leads travam e quanto isso custa." },
  { n: "02", t: "Desenho do processo", d: "Funil, etapas, scripts, cadência de follow-up e critérios de qualificação." },
  { n: "03", t: "Implantação do CRM SVG", d: "WhatsApp conectado, funil no ar e a IA treinada com as informações do seu negócio." },
  { n: "04", t: "Automação de atendimento", d: "IA respondendo 24h, qualificando, agendando e reativando oportunidades paradas." },
  { n: "05", t: "Gestão e evolução", d: "Indicadores, ajustes de script e treinamento da equipe para vender mais todo mês." },
];

const niches = [
  "Clínicas e consultórios", "Imobiliárias", "Concessionárias", "Escolas e cursos", "Estética e beleza", "Prestadores de serviço",
  "Construtoras", "E-commerce", "Assistências técnicas", "Academias", "Contabilidade e advocacia", "Franquias",
];

const differentials = [
  "IA treinada com o seu processo comercial",
  "Implantação acompanhada de ponta a ponta",
  "CRM próprio, feito para WhatsApp",
  "Consultoria comercial junto da tecnologia",
  "Follow-up automático que recupera vendas",
  "Indicadores claros de atendimento e fechamento",
  "Treinamento da equipe incluído",
  "Suporte humano de verdade",
];

const faq = [
  { q: "O que a SVG Multimídia faz exatamente?", a: "Implantamos automação de atendimento com inteligência artificial, o CRM SVG para WhatsApp e a consultoria comercial que estrutura funil, scripts, follow-up e fechamento. Somos especialistas em IA aplicada a processos comerciais." },
  { q: "A IA substitui minha equipe de vendas?", a: "Não. A IA cuida do volume repetitivo — responder na hora, qualificar, agendar e fazer follow-up — e entrega para o vendedor apenas o lead pronto para negociar." },
  { q: "Quanto tempo leva para implantar?", a: "A parte técnica é rápida. O treinamento da IA e a configuração do funil dependem do seu processo: normalmente de poucos dias a duas semanas." },
  { q: "Funciona com WhatsApp que já uso?", a: "Sim. Conectamos o número da empresa por API oficial ou não oficial, mantendo o histórico de atendimento organizado dentro do CRM." },
  { q: "A SVG também trabalha com sites e SEO?", a: "Sim, como serviços complementares para alimentar o comercial com leads. Você encontra as páginas de sites, SEO e Ficha do Google no rodapé do site." },
];

function Home() {
  const { data: latest = [] } = useQuery({
    queryKey: ["home-latest-posts"],
    queryFn: () => listLatestPosts({ data: { limit: 6 } }),
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
  });
  return (
    <>
      {/* HERO */}
      <section className="hero-bg relative overflow-hidden">
        <div className="container-svg pt-16 pb-20 md:pt-24 md:pb-28">
          <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <span className="eyebrow">Automação de atendimento · CRM com IA · Consultoria comercial</span>
              <h1 className="mt-5 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                <span className="text-gradient">Sua empresa atendendo 24h</span>, com IA no WhatsApp e um processo comercial que fecha vendas
              </h1>
              <p className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
                A SVG Multimídia implanta automação de atendimento com inteligência artificial, o CRM SVG para WhatsApp e a consultoria comercial que organiza funil, follow-up e fechamento — sem perder nenhum contato.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href={whatsappUrl()} target="_blank" rel="noopener" className="btn-primary">
                  Falar com um especialista
                </a>
                <Link to="/crm-yt" className="btn-ghost">Conhecer o CRM SVG</Link>
              </div>
              <dl className="mt-12 grid grid-cols-3 gap-6 border-t border-border pt-8">
                <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">Atendimento</dt><dd className="mt-1 font-display text-lg font-semibold">IA 24h no WhatsApp</dd></div>
                <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">Gestão</dt><dd className="mt-1 font-display text-lg font-semibold">CRM SVG</dd></div>
                <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">Vendas</dt><dd className="mt-1 font-display text-lg font-semibold">Processo estruturado</dd></div>
              </dl>
            </div>

            <HeroVisual />
          </div>
        </div>
      </section>

      {/* PROBLEMA */}
      <Section>
        <SectionHeader
          eyebrow="O Problema"
          title={<>O lead chega no WhatsApp.<br /><span className="text-primary">Mas alguém responde a tempo?</span></>}
          description="A maioria das empresas não perde venda por falta de cliente: perde por demora na resposta, follow-up que nunca acontece e um processo comercial que só existe na cabeça das pessoas."
        />
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {problems.map((p, i) => (
            <Reveal key={p.t} delay={((i % 3) + 1) as 1 | 2 | 3}>
              <Tilt3D>
                <div className="card-glass">
                  <div className="mb-3 h-1 w-10 rounded-full bg-[image:var(--gradient-primary)]" />
                  <h3 className="text-lg font-semibold">{p.t}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{p.d}</p>
                </div>
              </Tilt3D>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* SOLUÇÃO */}
      <Section className="bg-[color:var(--color-sidebar)]">
        <SectionHeader
          eyebrow="O que a SVG faz"
          title={<>Especialistas em <span className="text-primary">IA aplicada a processos comerciais</span></>}
          description="Unimos o CRM SVG com inteligência artificial, a automação de atendimento no WhatsApp e a consultoria comercial que organiza funil, follow-up e fechamento."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => (
            <Reveal key={s.t} delay={((i % 3) + 1) as 1 | 2 | 3}>
              <Tilt3D>
                <article className={`card-glass flex h-full flex-col overflow-hidden p-0 ${i === 0 ? "ring-2 ring-primary/60 shadow-[var(--shadow-glow)] lg:col-span-2" : ""}`}>
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={s.img}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      width={1024}
                      height={768}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    {i === 0 && (
                      <span className="mb-3 inline-flex w-fit rounded-full bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
                        Nossa especialidade
                      </span>
                    )}
                    <h3 className="text-lg font-semibold">{s.t}</h3>
                    <p className="mt-3 text-sm text-muted-foreground">{s.d}</p>
                    <Link to={s.to} className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
                      {s.cta} →
                    </Link>
                  </div>
                </article>
              </Tilt3D>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* MÉTODO */}
      <Section>
        <SectionHeader
          eyebrow="Método SVG"
          title={<>Um método para transformar <span className="text-primary">conversas no WhatsApp</span> em vendas previsíveis</>}
        />
        <ol className="mt-12 grid gap-4 md:grid-cols-5">
          {method.map((m, i) => (
            <Reveal key={m.n} as="li" delay={((i % 5) + 1) as 1 | 2 | 3 | 4 | 5}>
              <Tilt3D max={6}>
                <div className="card-glass">
                  <span className="font-display text-2xl font-bold text-primary">{m.n}</span>
                  <h3 className="mt-3 font-semibold">{m.t}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{m.d}</p>
                </div>
              </Tilt3D>
            </Reveal>
          ))}
        </ol>
      </Section>

      {/* POR QUE IA NO ATENDIMENTO */}
      <Section className="bg-[color:var(--color-sidebar)]">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <span className="eyebrow">Por que IA no atendimento</span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight md:text-4xl">Um funcionário que atende 24h, nunca esquece um lead e não tira férias</h2>
            <p className="mt-4 text-muted-foreground">
              A IA do CRM SVG responde em segundos, entende a intenção do cliente, qualifica, agenda e passa o bastão para o vendedor no momento certo — dentro do processo comercial que desenhamos com você.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {[
                "Resposta em menos de 1 minuto, todos os dias",
                "Qualificação e agendamento automáticos",
                "Follow-up que reativa oportunidades paradas",
                "Indicadores de atendimento, funil e fechamento",
              ].map((i) => (
                <li key={i} className="flex items-start gap-3"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary" />{i}</li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={whatsappUrl()} target="_blank" rel="noopener" className="btn-primary">Quero atender 24h com IA</a>
              <Link to="/crm-para-whatsapp" className="btn-ghost">Ver CRM para WhatsApp</Link>
            </div>
          </div>
          <ChatMockup />
        </div>
      </Section>

      {/* NICHOS */}
      <Section>
        <SectionHeader
          eyebrow="Para quem"
          title="Empresas que vendem por atendimento e precisam de processo comercial para escalar"
        />
        <div className="mt-10 flex flex-wrap gap-2">
          {niches.map((n) => (
            <span key={n} className="rounded-full border border-border bg-secondary px-4 py-2 text-sm">{n}</span>
          ))}
        </div>
      </Section>

      {/* DIFERENCIAIS */}
      <Section className="bg-[color:var(--color-sidebar)]">
        <SectionHeader eyebrow="Diferenciais" title="Por que escolher a SVG Multimídia" />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {differentials.map((d, i) => (
            <Reveal key={d} delay={((i % 4) + 1) as 1 | 2 | 3 | 4}>
              <Tilt3D max={6}>
                <div className="card-glass">
                  <span className="text-primary">✓</span>
                  <p className="mt-2 text-sm font-medium">{d}</p>
                </div>
              </Tilt3D>
            </Reveal>
          ))}
        </div>
      </Section>

      <CTASection />

      <ReviewsSection />

      <TeamSection />

      <VideosCarousel />

      {/* BLOG */}
      <Section>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <SectionHeader
            eyebrow="Conteúdo"
            title="Conteúdos sobre atendimento, automação com IA e vendas"
            description="Aprenda a estruturar o comercial da sua empresa e usar inteligência artificial para atender e vender melhor."
          />
          <Link to="/blog" className="btn-ghost">Ver todos os artigos</Link>
        </div>
        {latest.length === 0 ? (
          <p className="mt-10 text-sm text-muted-foreground">Em breve, novos artigos publicados pela equipe SVG.</p>
        ) : (
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {latest.map((p, i) => (
              <Reveal key={p.slug} delay={((i % 3) + 1) as 1 | 2 | 3}>
                <Tilt3D>
                  <Link to="/blog/$slug" params={{ slug: p.slug }} className="card-glass block">
                    {p.cover_image ? (
                      <img src={p.cover_image} alt={p.title} loading="lazy" className="mb-4 h-36 w-full rounded-xl object-cover" />
                    ) : (
                      <div className="mb-4 h-36 rounded-xl" style={{ background: p.cover }} />
                    )}
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary">{p.category}</span>
                    <h3 className="mt-2 text-lg font-semibold leading-snug">{p.title}</h3>
                    <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{p.excerpt || p.description}</p>
                    <p className="mt-4 text-xs text-muted-foreground">{formatDateBR(p.published_at ?? new Date().toISOString())} · {p.reading_time}</p>
                  </Link>
                </Tilt3D>
              </Reveal>
            ))}
          </div>
        )}
      </Section>

      <Section>
        <FAQ items={faq} />
      </Section>

      <Section>
        <Reveal>
          <div className="mb-6 max-w-2xl">
            <span className="eyebrow">Onde estamos</span>
            <h2 className="mt-3 text-2xl font-bold tracking-tight md:text-3xl">Venha nos visitar</h2>
            <p className="mt-2 text-sm text-muted-foreground md:text-base">
              Atendemos empresas em todo o Brasil a partir da nossa base. Agende uma reunião presencial ou online.
            </p>
          </div>
          <div className="overflow-hidden rounded-2xl border border-border bg-secondary/40 shadow-lg">
            <iframe
              title="Localização SVG Multimídia no Google Maps"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3543.5752393475505!2d-53.39296752454501!3d-27.357744976384748!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94fb75495d068541%3A0x3e6375ddb83b2096!2sSVG%20Multim%C3%ADdia%20%7C%20Ag%C3%AAncia%20de%20marketing%20digital%20%7C%20Sites%20%7C%20Gest%C3%A3o%20de%20tr%C3%A1fego!5e0!3m2!1spt-BR!2sbr!4v1782458161332!5m2!1spt-BR!2sbr"
              width="100%"
              height="380"
              style={{ border: 0, display: "block" }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        </Reveal>
      </Section>
    </>
  );
}

function HeroVisual() {
  return (
    <div className="relative">
      <div className="absolute -inset-10 -z-10 rounded-[2.5rem] bg-[radial-gradient(60%_60%_at_50%_50%,oklch(0.62_0.22_258/0.35),transparent)]" />
      <div className="relative overflow-hidden rounded-2xl border border-border bg-background/40 shadow-[var(--shadow-card)]">
        <img
          src={heroSeo}
          alt="Ilustração 3D de um processo comercial completo: site otimizado captando leads, funil com automação e inteligência artificial no WhatsApp e fechamento de negócio com crescimento de receita."
          width={1536}
          height={1024}
          fetchPriority="high"
          decoding="async"
          className="block aspect-[3/2] w-full object-cover"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-background/70 via-background/10 to-transparent" />

        {/* Floating metric card */}
        <div className="float-slow absolute left-4 top-4 rounded-xl border border-border shadow-[var(--shadow-soft)] bg-background/80 px-3 py-2 text-xs backdrop-blur">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Leads qualificados</p>
          <p className="font-display text-lg font-bold text-primary">+184%</p>
        </div>

        {/* Floating presence pin with pulse */}
        <div className="absolute bottom-5 right-5 flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary backdrop-blur">
          <span className="relative grid h-2 w-2 place-items-center">
            <span className="absolute inset-0 rounded-full bg-primary/60 pulse-ring" />
            <span className="relative h-2 w-2 rounded-full bg-primary" />
          </span>
          Processo rodando 24/7
        </div>
      </div>

      {/* Floating growth card — overlaps the image edge and the page background */}
      <div className="float-slower absolute -right-3 top-6 z-10 rounded-xl border border-border shadow-[var(--shadow-card)] bg-background/90 px-3 py-2 text-xs backdrop-blur md:-right-5">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Tempo de resposta</p>
        <p className="font-display text-lg font-bold text-primary">&lt; 1 min</p>
      </div>
    </div>
  );
}

function ChatMockup() {
  const messages = [
    { from: "cliente" as const, t: "Oi, vocês atendem hoje ainda?" },
    { from: "ia" as const, t: "Olá! Atendemos sim 😊 Me conta rapidinho o que você precisa que eu já verifico a agenda." },
    { from: "cliente" as const, t: "Preciso de um orçamento pra amanhã de manhã." },
    { from: "ia" as const, t: "Perfeito! Tenho 9h ou 10h30. Qual fica melhor pra você?" },
    { from: "cliente" as const, t: "Pode ser 9h" },
    { from: "ia" as const, t: "Agendado às 9h ✅ Já avisei o consultor responsável. Te mando um lembrete antes." },
  ];
  return (
    <Tilt3D>
      <div className="card-glass">
        <div className="flex items-center justify-between gap-3 border-b border-border pb-4">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-[image:var(--gradient-primary)] text-sm font-bold text-primary-foreground">IA</span>
            <div>
              <p className="text-sm font-semibold">Atendimento SVG · WhatsApp</p>
              <p className="text-xs text-primary">online agora</p>
            </div>
          </div>
          <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-primary">24h</span>
        </div>
        <ul className="mt-4 space-y-3">
          {messages.map((m, i) => (
            <li key={i} className={m.from === "ia" ? "flex justify-end" : "flex justify-start"}>
              <p
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${
                  m.from === "ia"
                    ? "rounded-br-md bg-primary/10 text-foreground ring-1 ring-primary/20"
                    : "rounded-bl-md border border-border bg-background/60 text-muted-foreground"
                }`}
              >
                {m.t}
              </p>
            </li>
          ))}
        </ul>
        <p className="mt-5 border-t border-border pt-4 text-xs text-muted-foreground">
          Conversa registrada no funil do CRM SVG, com o lead qualificado e a visita agendada automaticamente.
        </p>
      </div>
    </Tilt3D>
  );
}
